/**
 * Founding Explorer Landing Page
 * Exclusive offer for the first 200 users - lifetime premium access
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Crown, Check, Sparkles, Globe, Users, Map, Zap, Star,
  ArrowLeft, Loader2, Lock, Gift, Shield, MessageSquare
} from "lucide-react";
import Footer from "@/components/Footer";
import { FoundingBadgeCard } from "@/components/FoundingBadge";

const BENEFITS = [
  {
    icon: Crown,
    title: "Lifetime Premium Access",
    description: "All premium features, forever. No monthly fees, ever.",
    color: "text-amber-500"
  },
  {
    icon: Star,
    title: "Permanent Gold Badge",
    description: "Exclusive Founding Explorer badge displayed everywhere.",
    color: "text-yellow-500"
  },
  {
    icon: Zap,
    title: "Early Access",
    description: "Be the first to try new features before anyone else.",
    color: "text-primary"
  },
  {
    icon: MessageSquare,
    title: "Private Feedback Channel",
    description: "Direct line to shape the future of DuoLyst.",
    color: "text-secondary"
  },
  {
    icon: Map,
    title: "Exclusive Map Theme",
    description: "Gold-outlined founder map theme. Never sold again.",
    color: "text-amber-600"
  },
  {
    icon: Shield,
    title: "Founding Wall Recognition",
    description: "Your name immortalized on the Founding Wall.",
    color: "text-purple-500"
  }
];

const PREMIUM_FEATURES = [
  "Unlimited bucket list items",
  "AI-powered trip suggestions",
  "Advanced budget tracking",
  "Unlimited photo uploads",
  "PDF trip exports",
  "Public share links",
  "Custom categories",
  "Priority support",
  "Partner & group features",
  "World Map analytics"
];

export default function FoundingExplorerPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const endpoint = token ? "/founding/status" : "/founding/status/public";
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      const res = await axios.get(`${API}${endpoint}`, config);
      setStatus(res.data);
    } catch (error) {
      console.error("Failed to fetch founding status:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleClaim = async () => {
    if (!user) {
      toast.error("Please create an account first");
      navigate("/register?redirect=/founding-explorer");
      return;
    }

    setClaiming(true);
    try {
      const res = await axios.post(
        `${API}/founding/claim`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(res.data.message, {
        duration: 5000,
        icon: "🎉"
      });
      
      // Refresh status
      fetchStatus();
      
      // Celebrate!
      confetti();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to claim spot";
      toast.error(message);
    } finally {
      setClaiming(false);
    }
  };

  // Simple confetti effect
  const confetti = () => {
    const colors = ['#fbbf24', '#f59e0b', '#d97706', '#ffffff'];
    for (let i = 0; i < 50; i++) {
      const confettiPiece = document.createElement('div');
      confettiPiece.style.cssText = `
        position: fixed;
        width: 10px;
        height: 10px;
        background: ${colors[Math.floor(Math.random() * colors.length)]};
        left: ${Math.random() * 100}vw;
        top: -10px;
        border-radius: ${Math.random() > 0.5 ? '50%' : '0'};
        pointer-events: none;
        z-index: 9999;
        animation: confetti-fall ${2 + Math.random() * 2}s linear forwards;
      `;
      document.body.appendChild(confettiPiece);
      setTimeout(() => confettiPiece.remove(), 4000);
    }
  };

  const progressPercent = status 
    ? Math.round((status.spots_claimed / status.total_spots) * 100) 
    : 0;

  const spotsRemaining = status?.spots_remaining ?? 200;
  const isClosed = spotsRemaining === 0;
  const isUserFounder = status?.user_is_founder;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Confetti animation keyframes */}
      <style>{`
        @keyframes confetti-fall {
          to {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
        @keyframes shimmer {
          to {
            transform: translateX(200%);
          }
        }
        .animate-shimmer {
          animation: shimmer 3s infinite;
        }
        .animate-pulse-subtle {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      `}</style>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Animated background */}
        <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 via-yellow-500/5 to-background" />
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-10 w-72 h-72 bg-amber-400/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-yellow-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 py-16 md:py-24 text-center">
          <Link 
            to="/" 
            className="inline-flex items-center text-muted-foreground hover:text-foreground mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>

          {/* Badge */}
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/20 border border-amber-400/30">
              <Gift className="w-4 h-4 text-amber-500" />
              <span className="text-sm font-medium text-amber-700 dark:text-amber-400">
                Limited Time Offer
              </span>
            </div>
          </div>

          {/* Badge Image */}
          <div className="flex justify-center mb-8">
            <img 
              src="/founding-explorer-badge.png" 
              alt="Founding Explorer Badge"
              className="h-32 md:h-40 w-auto object-contain drop-shadow-2xl animate-pulse-subtle"
            />
          </div>

          {/* Title */}
          <h1 className="font-serif text-4xl md:text-6xl font-semibold text-foreground mb-4">
            The Founding
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-600">
              200
            </span>
          </h1>

          <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
            Be one of the original 200 explorers. Get lifetime premium access, an exclusive badge, 
            and your name on the Founding Wall. <strong>Forever.</strong>
          </p>

          {/* Counter */}
          <div className="max-w-md mx-auto mb-8">
            <div className="bg-card border border-border rounded-2xl p-6 shadow-lg">
              {isClosed ? (
                <div className="text-center">
                  <Lock className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-lg font-semibold text-foreground">
                    All spots have been claimed
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    This offer is permanently closed
                  </p>
                </div>
              ) : (
                <>
                  <div className="flex justify-between items-end mb-3">
                    <div>
                      <p className="text-sm text-muted-foreground">Spots Remaining</p>
                      <p className="text-4xl font-bold text-amber-500">
                        {spotsRemaining}
                        <span className="text-lg text-muted-foreground font-normal">
                          /{status?.total_spots}
                        </span>
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Claimed</p>
                      <p className="text-2xl font-semibold text-foreground">
                        {status?.spots_claimed || 0}
                      </p>
                    </div>
                  </div>
                  <Progress 
                    value={progressPercent} 
                    className="h-3 bg-muted"
                  />
                  <p className="text-xs text-muted-foreground mt-2 text-center">
                    {progressPercent}% claimed • Once filled, it's gone forever
                  </p>
                </>
              )}
            </div>
          </div>

          {/* CTA Button */}
          {isUserFounder ? (
            <div className="max-w-md mx-auto">
              <FoundingBadgeCard 
                founderNumber={status?.user_joined_at ? null : 1} // We'd need the number from status
                joinedAt={status?.user_joined_at}
              />
              <p className="text-sm text-muted-foreground mt-4">
                You're already a Founding Explorer! 🎉
              </p>
            </div>
          ) : isClosed ? (
            <Button size="lg" disabled className="rounded-full px-10 py-6">
              <Lock className="w-5 h-5 mr-2" />
              Offer Closed Forever
            </Button>
          ) : (
            <Button
              size="lg"
              onClick={handleClaim}
              disabled={claiming}
              className="bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-600 hover:from-amber-600 hover:via-yellow-600 hover:to-amber-700 text-white rounded-full px-10 py-6 text-lg font-semibold shadow-lg shadow-amber-500/30 transition-all hover:shadow-xl hover:shadow-amber-500/40"
              data-testid="claim-founding-btn"
            >
              {claiming ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Claiming...
                </>
              ) : (
                <>
                  <Crown className="w-5 h-5 mr-2" />
                  Claim Your Spot — Free
                </>
              )}
            </Button>
          )}

          {!user && !isClosed && (
            <p className="text-sm text-muted-foreground mt-4">
              <Link to="/register?redirect=/founding-explorer" className="text-primary hover:underline">
                Create a free account
              </Link>
              {" "}to claim your spot
            </p>
          )}
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-16 px-4 bg-muted/30">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-serif text-3xl font-semibold text-foreground text-center mb-12">
            What You Get
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {BENEFITS.map((benefit) => (
              <div
                key={benefit.title}
                className="bg-card rounded-2xl p-6 border border-border shadow-sm hover:shadow-md transition-shadow"
              >
                <div className={`w-12 h-12 rounded-xl bg-muted flex items-center justify-center mb-4 ${benefit.color}`}>
                  <benefit.icon className="w-6 h-6" />
                </div>
                <h3 className="font-semibold text-foreground mb-2">{benefit.title}</h3>
                <p className="text-sm text-muted-foreground">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Premium Features List */}
      <section className="py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-serif text-3xl font-semibold text-foreground text-center mb-4">
            Full Premium Access Includes
          </h2>
          <p className="text-center text-muted-foreground mb-10">
            Everything you need to plan and track your adventures
          </p>

          <div className="grid sm:grid-cols-2 gap-3">
            {PREMIUM_FEATURES.map((feature) => (
              <div
                key={feature}
                className="flex items-center gap-3 p-3 rounded-xl bg-secondary/10"
              >
                <div className="w-6 h-6 rounded-full bg-secondary/20 flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-secondary" />
                </div>
                <span className="text-foreground">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 px-4 bg-muted/30">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-serif text-3xl font-semibold text-foreground mb-4">
            Our Promise
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            When we hit 200, we close this offer <strong>permanently</strong>. 
            We will never reopen it. No exceptions. No "special" reopenings.
          </p>
          <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-card border border-border">
            <Shield className="w-5 h-5 text-primary" />
            <span className="text-foreground font-medium">
              200 spots. Forever. No exceptions.
            </span>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      {!isClosed && !isUserFounder && (
        <section className="py-16 px-4">
          <div className="max-w-xl mx-auto text-center">
            <h2 className="font-serif text-2xl font-semibold text-foreground mb-4">
              Ready to Join the Founding 200?
            </h2>
            <Button
              size="lg"
              onClick={handleClaim}
              disabled={claiming || !status?.is_available}
              className="bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-600 hover:from-amber-600 hover:via-yellow-600 hover:to-amber-700 text-white rounded-full px-10 py-6 text-lg font-semibold shadow-lg shadow-amber-500/30"
              data-testid="claim-founding-btn-bottom"
            >
              {claiming ? (
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              ) : (
                <Crown className="w-5 h-5 mr-2" />
              )}
              {user ? "Claim Your Spot Now" : "Create Account & Claim"}
            </Button>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
